import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import AdminLayout from "../../components/AdminLayout";
import { api } from "../../lib/api";

const STATUSES = ["placed", "confirmed", "printed", "shipped", "delivered"];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [courier, setCourier] = useState("");
  const [tracking, setTracking] = useState("");
  const [newCount, setNewCount] = useState(0);

  const load = async () => {
    const r = await api.get("/admin/orders");
    setOrders(r.data);
    const placed = r.data.filter((o) => o.status === "placed").length;
    setNewCount(placed);
  };
  useEffect(() => { load(); }, []);

  const clearAll = async () => {
    if (!window.confirm("Delete ALL orders & payment transactions? This cannot be undone.")) return;
    try {
      const r = await api.delete("/admin/orders");
      toast.success(`CLEARED ${r.data.deleted_orders} ORDERS`);
      setSelected(null);
      load();
    } catch (err) {
      toast.error("FAILED", { description: err?.response?.data?.detail || "" });
    }
  };

  const updateStatus = async (orderId, newStatus) => {
    if (newStatus === "shipped") {
      if (!courier.trim() || !tracking.trim()) {
        toast.error("ENTER COURIER + TRACKING NUMBER FIRST");
        return;
      }
    }
    try {
      const params = new URLSearchParams({ new_status: newStatus });
      if (newStatus === "shipped") {
        params.append("courier_name", courier);
        params.append("tracking_number", tracking);
      }
      await api.put(`/admin/orders/${orderId}/status?${params.toString()}`);
      toast.success(`STATUS → ${newStatus.toUpperCase()} // EMAIL SENT`);
      load();
      if (selected?.id === orderId) {
        const updated = (await api.get(`/orders/${orderId}`)).data;
        setSelected(updated);
        setCourier(updated.courier_name || "");
        setTracking(updated.tracking_number || "");
      }
    } catch (err) {
      toast.error("FAILED", { description: err?.response?.data?.detail || "" });
    }
  };

  const onSelect = (o) => {
    setSelected(o);
    setCourier(o.courier_name || "");
    setTracking(o.tracking_number || "");
  };

  return (
    <AdminLayout title="Orders">
      <div className="flex justify-between items-center mb-4">
        <p className="label-mono text-[#71717A] flex items-center gap-3">
          {orders.length} ORDER{orders.length !== 1 ? "S" : ""}
          {newCount > 0 && (
            <span className="bg-[#CFF048] text-black text-[10px] font-mono font-bold px-2 py-0.5" data-testid="new-orders-badge">
              {newCount} NEW
            </span>
          )}
        </p>
        {orders.length > 0 && (
          <button onClick={clearAll} className="label-mono px-3 py-2 border border-red-900 text-red-500 hover:bg-red-950 flex items-center gap-2" data-testid="clear-all-orders-btn">
            <Trash2 size={14} /> CLEAR ALL DEMO ORDERS
          </button>
        )}
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-[#0A0A0A] border border-[#27272A] overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-orders-table">
            <thead className="border-b border-[#27272A]">
              <tr className="label-mono text-[#71717A]">
                <th className="text-left p-4">ORDER</th>
                <th className="text-left p-4">CUSTOMER</th>
                <th className="text-left p-4">PRODUCTS</th>
                <th className="text-left p-4">TOTAL</th>
                <th className="text-left p-4">PAYMENT</th>
                <th className="text-left p-4">STATUS</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const productNames = (o.items || []).map((it) => it.name).filter(Boolean).join(", ");
                return (
                  <tr key={o.id} onClick={() => onSelect(o)} className={`border-b border-[#18181B] cursor-pointer hover:bg-[#111] ${selected?.id === o.id ? "bg-[#111]" : ""}`}>
                    <td className="p-4">
                      <div className="font-mono-cult text-[#CFF048]">{o.id.slice(0,8).toUpperCase()}</div>
                      {o.status === "placed" && (
                        <span className="label-mono text-[10px] bg-[#CFF048] text-black px-1.5">NEW</span>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="font-display font-bold text-sm">{o.user_name || "—"}</div>
                      <div className="label-mono text-[#71717A] text-[10px]">{o.user_email}</div>
                    </td>
                    <td className="p-4 text-xs text-[#A1A1AA] max-w-[180px] truncate">{productNames || "—"}</td>
                    <td className="p-4 font-mono-cult">₹{o.total.toFixed(0)}</td>
                    <td className="p-4 label-mono">{(o.payment_status || "pending").toUpperCase()}</td>
                    <td className="p-4 label-mono text-[#CFF048]">{o.status.toUpperCase()}</td>
                  </tr>
                );
              })}
              {orders.length === 0 && <tr><td colSpan={6} className="p-8 text-center label-mono text-[#71717A]">// NO ORDERS</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="bg-[#0A0A0A] border border-[#27272A] p-5 h-fit">
          <p className="label-mono text-[#CFF048]">// ORDER DETAIL</p>
          {!selected ? (
            <p className="label-mono text-[#71717A] mt-4">SELECT AN ORDER →</p>
          ) : (
            <div className="mt-4 text-sm">
              <p className="font-display font-bold text-lg">#{selected.id.slice(0,8).toUpperCase()}</p>
              <p className="label-mono text-[#71717A] mt-1">{selected.user_email}</p>
              <p className="font-mono-cult text-[#CFF048] mt-2">₹{selected.total.toFixed(2)}</p>

              {/* SHIPPING (REQUIRED FOR SHIPPED STATUS) */}
              <div className="mt-4 border-t border-[#27272A] pt-4">
                <p className="label-mono text-[#71717A] mb-2">COURIER + TRACKING (required for SHIPPED)</p>
                <input
                  className="cult-input mb-2"
                  placeholder="COURIER (e.g. Delhivery, Bluedart)"
                  value={courier}
                  onChange={(e) => setCourier(e.target.value)}
                  data-testid="courier-input"
                />
                <input
                  className="cult-input"
                  placeholder="TRACKING NUMBER"
                  value={tracking}
                  onChange={(e) => setTracking(e.target.value)}
                  data-testid="tracking-input"
                />
              </div>

              <div className="mt-4">
                <p className="label-mono text-[#71717A] mb-2">UPDATE STATUS</p>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => updateStatus(selected.id, s)}
                      className={`px-3 py-1.5 label-mono border transition-colors ${selected.status === s ? "bg-[#CFF048] text-black border-[#CFF048]" : "border-[#27272A] hover:border-white"}`}
                      data-testid={`status-btn-${s}`}
                    >{s.toUpperCase()}</button>
                  ))}
                </div>
                <p className="label-mono text-[#71717A] mt-3 text-[10px]">EACH CLICK SENDS AN EMAIL TO THE BUYER</p>
              </div>
              <div className="mt-4">
                <p className="label-mono text-[#71717A] mb-2">ITEMS</p>
                {selected.items.map((it, i) => (
                  <div key={i} className="flex items-center gap-2 border-b border-[#18181B] py-2">
                    <img src={it.image} alt="" className="w-10 h-10 object-cover" />
                    <div className="flex-1">
                      <div className="text-xs font-display">{it.name}</div>
                      <div className="label-mono text-[#71717A]">{it.size}/{it.color} × {it.qty}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <p className="label-mono text-[#71717A] mb-1">SHIPPING</p>
                <p className="text-xs">{selected.shipping.full_name}, {selected.shipping.line1}, {selected.shipping.city}</p>
                <p className="label-mono text-[#71717A]">{selected.shipping.phone}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
